import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {To, useLocation, useNavigate} from 'react-router-dom';
import {usePath} from 'hooks';
import {goToExternalLink} from 'utils/common';
import {PATHS} from 'Routes';
import {SessionStorage} from '@guestapp/core';

type ContextProps = {
  isOpenDiscardModal: boolean;
  setLinkToGo: Dispatch<SetStateAction<string>>;
  setIsFormTouched: Dispatch<SetStateAction<boolean>>;
  closeDiscardModal: () => void;
  setShouldOpenDiscardModal: Dispatch<SetStateAction<boolean>>;
  discardChangesAndRestart: () => void;
  linkToGo: string;
  isFormTouched: boolean;
  checkIfOpenDiscardModalAndRedirect: (link: string) => void;
};

function getDefLinkToGo(link: string) {
  return link === '-1' ? -1 : link;
}

const OpenModalsContext = createContext<ContextProps>({
  isOpenDiscardModal: false,
  setLinkToGo: () => {},
  setIsFormTouched: () => {},
  closeDiscardModal: () => {},
  setShouldOpenDiscardModal: () => {},
  discardChangesAndRestart: () => {},
  linkToGo: '',
  isFormTouched: false,
  checkIfOpenDiscardModalAndRedirect: () => {},
});

function OpenModalsProvider({children}: PropsWithChildren) {
  const [isOpenDiscardModal, setIsOpenDiscardModal] = useState(false);
  const [isFormTouched, setIsFormTouched] = useState(false);
  const [linkToGo, setLinkToGo] = useState('');
  const [shouldOpenDiscardModal, setShouldOpenDiscardModal] = useState(false);
  const {getFullPath} = usePath();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const closeDiscardModal = useCallback(() => {
    setIsOpenDiscardModal(false);
    setShouldOpenDiscardModal(false);
    setLinkToGo('');
  }, []);

  const goToLink = useCallback(
    (link: string) => {
      const isExternalLink = link.includes('https');

      if (isExternalLink) {
        goToExternalLink(link);
      } else {
        navigate(getDefLinkToGo(link) as To);
      }
    },
    [navigate],
  );

  const discardChangesAndRestart = useCallback(() => {
    SessionStorage.clear();
    closeDiscardModal();
    goToLink(linkToGo);
  }, [goToLink, linkToGo, closeDiscardModal]);

  useEffect(() => {
    if (shouldOpenDiscardModal && isFormTouched) {
      setIsOpenDiscardModal(true);
    } else {
      if (Boolean(linkToGo) && !isFormTouched) {
        setShouldOpenDiscardModal(false);
        goToLink(linkToGo);
        setLinkToGo('');
      }
    }
  }, [goToLink, isFormTouched, linkToGo, shouldOpenDiscardModal]);

  const checkIfOpenDiscardModalAndRedirect = useCallback(
    (link: string) => {
      const linkToGo = link.includes('https') || link === '-1' ? link : getFullPath(link);

      if (
        pathname.includes(PATHS.addPersonalDataForm) ||
        pathname.includes(PATHS.verification.main)
      ) {
        setLinkToGo(linkToGo);
        setShouldOpenDiscardModal(true);
        return;
      }
      setShouldOpenDiscardModal(false);

      goToLink(linkToGo);
    },
    [getFullPath, goToLink, pathname],
  );

  const defaultProps = useMemo<ContextProps>(
    () => ({
      isOpenDiscardModal,
      closeDiscardModal,
      setIsFormTouched,
      isFormTouched,
      setLinkToGo,
      setShouldOpenDiscardModal,
      discardChangesAndRestart,
      linkToGo,
      checkIfOpenDiscardModalAndRedirect,
    }),
    [
      checkIfOpenDiscardModalAndRedirect,
      closeDiscardModal,
      discardChangesAndRestart,
      isFormTouched,
      isOpenDiscardModal,
      linkToGo,
    ],
  );

  return <OpenModalsContext.Provider value={defaultProps} children={children} />;
}

export {OpenModalsContext, OpenModalsProvider};
